import {
  getEstablishmentProfile,
  getPharmacySettings,
  getReceptionSettings,
  saveEstablishmentProfile,
  savePharmacySettings,
  saveReceptionSettings,
} from "@/core/admin/establishment-store";
import {
  completeHospitolSetup,
  fetchHospitolSetupStatus,
} from "@/core/setup/hospitol-setup-store";

import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from "@/core/tenant/hospitol-storage";

const ADMISSIONS_KEY = "hospitol.admissions";

/** Clés localStorage des seeds démo — pour reset navigateur (prod / présentation). */
export const DEMO_SEED_STORAGE_KEYS = [
  "hospitol.demoRichSeeded",
  "hospitol.treasury.demoSeeded",
  "hospitol.demoCommercialSeeded",
  "hospitol.pharmacyFulfillment.demoSeeded",
  "hospitol.patients.demoSeeded",
  "hospitol.clinical.demoSeeded",
  "hospitol.admissions.demoSeeded",
  "hospitol.lab.demoSeeded",
  "hospitol.imaging.demoSeeded",
  "hospitol.insurers.demoSeeded",
  "hospitol.admin.demoSeeded",
  "hospitol.serviceQueue.demoSeeded",
  "hospitol.appointments.demoSeeded",
  "hospitol.hospitalization.demoSeeded",
] as const;

export function resetAllDemoSeedMarkers(): void {
  for (const key of DEMO_SEED_STORAGE_KEYS) {
    removeStorageItem(key);
  }
}

/** Marque le wizard d'installation comme terminé (présentation prod sans écran setup). */
export function seedDemoPresentationReady(): void {
  seedDemoEstablishmentProfile();
  if (!fetchHospitolSetupStatus().complete) {
    completeHospitolSetup();
  }
}

export function patchAdmissionTimestamps(
  admissionIds: string[],
  baseDate: Date,
  startHour = 7,
): void {
  if (admissionIds.length === 0) return;
  try {
    const raw = getStorageItem(ADMISSIONS_KEY);
    if (!raw) return;
    const list = JSON.parse(raw) as Array<Record<string, unknown>>;
    const idSet = new Set(admissionIds);
    let hour = startHour;
    const dayStart = new Date(baseDate);
    dayStart.setHours(0, 0, 0, 0);

    const updated = list.map((row) => {
      const id = String(row.id ?? "");
      if (!idSet.has(id)) return row;
      const at = new Date(dayStart);
      at.setHours(hour, 15 + (hour % 4) * 10, 0, 0);
      hour += 1;
      return { ...row, createdAt: at.toISOString(), updatedAt: at.toISOString() };
    });

    setStorageItem(ADMISSIONS_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("hospitol:admissions"));
  } catch {
    // ignore corrupt cache
  }
}

/** Profil établissement crédible pour Centre Médical Salama (setup + affichage). */
export function seedDemoEstablishmentProfile(): void {
  const profile = getEstablishmentProfile();
  if (profile.name.includes("Salama") && profile.address.length > 10) return;

  saveEstablishmentProfile({
    name: "Centre Médical Salama",
    address: "45 Avenue de la Libération, Commune de la Gombe, Kinshasa",
    phone: "+243 81 500 1000",
    email: "contact@cms-salama.demo",
  });

  const pharmacy = getPharmacySettings();
  savePharmacySettings({
    ...pharmacy,
    currency: "CDF",
    invoicePrefix: "CMS",
    requirePatientOnInvoice: true,
    syncBillingOnInvoice: true,
  });

  const reception = getReceptionSettings();
  if (reception.generalConsultationFee <= 0) {
    saveReceptionSettings({
      ...reception,
      allowReceptionConsultationProforma: true,
      generalConsultationFee: 15_000,
    });
  }
}
